import React, { useMemo } from "react";
import { createColumnHelper, SortingState, Table } from "@tanstack/react-table";
import { PaneBody } from "@/components/pane/paneBody";
import { useTransfers, Transfer } from "@/hooks/useTransfers";
import { HighlightedValueChange } from "@/components/shared/highlightedValueChange";
import { Loader } from "@/components/shared/loader";
import { applyFilters, cn, formatAddress } from "@/lib/utils";
import { IColumn, IRow } from "@/domain/interfaces/sfTable.interface";
import dayjs from "dayjs";
import { PaneSubHeader } from "../pane/paneSubHeader";
import { useGrid } from "@/hooks/useGrid";
import { usePaneContext } from "../pane/paneContext";
import { Copy } from "lucide-react";
import { toast } from "@/components/ui/use-toast";

const columnHelper = createColumnHelper<Transfer>();

const oldValues: IRow[] = [];
const highlightTimestamps: number[] = [];

const copyToClipboard = (text: string) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      toast({
        title: "Copied to clipboard",
        description: `Address ${formatAddress(text)} copied to clipboard`,
        duration: 3000,
      });
    })
    .catch((err) => {
      console.error("Failed to copy text: ", err);
      toast({
        title: "Failed to copy",
        description: "Could not copy address to clipboard",
        variant: "destructive",
        duration: 3000,
      });
    });
};

function rowClassCb(
  row: Transfer,
  idx: number,
  table: Table<IRow>,
  sortingState: SortingState,
) {
  const rowTime = new Date(row.block_time).getTime();
  const color = "bg-green-600/20";
  if (rowTime > Date.now() - 10 * 60000) {
    return cn(color, "animate-pulse");
  }
  return "";
}

const columns = [
  columnHelper.accessor("symbol", {
    header: "Token",
    cell: (info) => (
      <div className="flex items-center gap-2">
        <img
          src={info.row.original.icon}
          alt={info.getValue()}
          className="w-5 h-5 rounded-full"
        />
        <span>{info.getValue()}</span>
      </div>
    ),
  }),
  columnHelper.accessor("from_entity", {
    header: "From",
    cell: (info) => {
      const address = info.getValue();
      return (
        <div className="flex items-center gap-1">
          <Copy
            className="h-3.5 w-3.5 cursor-pointer text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(address);
            }}
          />
          <span>{formatAddress(address)}</span>
        </div>
      );
    },
  }),
  columnHelper.accessor("to_entity", {
    header: "To",
    cell: (info) => {
      const address = info.getValue();
      return (
        <div className="flex items-center gap-1">
          <Copy
            className="h-3.5 w-3.5 cursor-pointer text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              copyToClipboard(address);
            }}
          />
          <span>{formatAddress(address)}</span>
        </div>
      );
    },
  }),
  columnHelper.accessor("value", {
    header: "Amount",
    cell: (info) => {
      const value = info.getValue();
      return (
        <HighlightedValueChange
          value={value}
          highlightNewTextColor={false}
          highlightPosNeg={false}
          className="font-bold"
          shorten={true}
        />
      );
    },
  }),
  columnHelper.accessor("value_usd", {
    header: "Amount USD",
    cell: (info) => {
      const value = info.getValue();
      return (
        <HighlightedValueChange
          value={value}
          highlightNewTextColor={false}
          highlightPosNeg={false}
          className="font-bold"
          shorten={true}
          prefix="$"
        />
      );
    },
  }),
  columnHelper.accessor("block_time", {
    header: "Time",
    cell: (info) => dayjs.utc(info.getValue()).fromNow(),
  }),
];

export function WhalesActivityPaneBody() {
  const { data: transfers, isLoading } = useTransfers();
  const nodeId = usePaneContext();
  const { getNodeConfig } = useGrid(nodeId);
  const config = getNodeConfig();
  const rows = useMemo(() => {
    if (!transfers) {
      return [];
    }

    if (config?.filters && config?.filterValues) {
      return applyFilters<Transfer>(
        transfers,
        config.filters,
        config.filterValues,
      );
    }
    return transfers || [];
  }, [transfers, config]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <PaneSubHeader />
      <PaneBody
        className="!h-[calc(100%-24px)]"
        tableColumns={columns as IColumn[]}
        data={rows}
        rowClickable={false}
        rowClassCb={(...args) => rowClassCb(...args, config.sortingState)}
      />
    </>
  );
}
